import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Customer, CustomerContact, CustomerPrice, FrameworkContract, FrameworkContractSource, ServiceItem, Quote, QuoteStatus, QuoteConfig } from '../../types';
import {
  FRAMEWORK_VALIDITY_LABELS, FrameworkValidity, frameworkContractValidity, frameworkItemNetUnitPrice, isFrameworkAcceptedQuote,
  mergeFrameworkContractsOnSave,
} from '../../services/frameworkContractLogic';
import { updateCustomer, updateCustomerMergingFrameworkContracts, fetchCustomerPrices, upsertCustomerPrice, deleteCustomerPrice, fetchQuotesForCustomer, fetchProjectsForCustomer, fetchCustomers } from '../../services/crmService';
import { fetchInvoicesForCustomer, getInvoicePdfUrl } from '../../services/invoiceService';
import { InvoiceStatusBadge } from '../invoices/InvoiceStatusBadge';
import { formatCents } from '../../services/money';
import { localToday } from '../../services/localToday';
import { Invoice } from '../../types';
import { resolveUnitPrice, isQuoteExpired, quoteAmountSuffix } from '../../services/quoteLogic';
import { appendChangeEntry } from '../../services/customerChangeLog';
import { personNameParts, planCustomerUpdate, withPersonNamePart } from '../../services/customerQuickEdit';
import { composePersonName, parsePersonName } from '../../services/personName';
import { collectMergedSourceIds } from '../../services/customerStats';
import { BRANCHE_OPTIONS } from '../../services/branchen';
import { formatCoordinates, geoQualityLabel } from '../../services/geoCache';
import { supabase } from '../../services/supabaseClient';
import { describeError } from '../../services/errorMessage';
import { rechtFehlerOder, rechtFehltHinweis } from '../../services/rechte/rechtFehler';
import { istNativeApp } from '../../services/nativePlatform';
import { DEFAULT_INVOICE_CONFIG } from '../../services/invoiceConfig';
import { Button } from '../Button';
import {
  ArrowLeft, Save, Loader2, Check, Building2, Mail, Phone, Globe, MapPin,
  User, Plus, Trash2, Users, Sliders, Star, Tag, History, RotateCcw, FileText, FolderOpen,
  CheckCircle2, AlertTriangle, Search, Link2, X, ChevronDown, ChevronRight
} from 'lucide-react';
import { CustomerRatingStars } from './CustomerRatingStars';
import { verifyAddress, parseLegacyAddress } from './verifyAddress';

const eur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const fmtEur = (n: number) => eur.format(Number.isFinite(n) ? n : 0);
const fmtDate = (v?: string) => (v ? new Date(v).toLocaleDateString('de-DE') : '—');
const fmtDateDmy = (v?: string) =>
  (v ? new Date(v).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const fmtPercent = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 2 });

// Gueltigkeits-Chip eines Rahmenvertrags. Gerechnet wird aus dem Entwurf,
// damit der Chip beim Aendern der Daten sofort mitgeht.
const FRAMEWORK_VALIDITY_CLS: Record<FrameworkValidity, string> = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  expired: 'bg-slate-100 text-slate-500 border-slate-200',
  upcoming: 'bg-blue-50 text-blue-700 border-blue-200',
};

/** Herkunft eines Rahmenvertrags aus einer Angebotsannahme. */
const FrameworkSourceLine: React.FC<{
  source: FrameworkContractSource;
  onOpenQuote?: (quoteId: string) => void;
}> = ({ source, onOpenQuote }) => (
  <span className="text-xs text-slate-500">
    Aus Angebot{' '}
    {onOpenQuote ? (
      <button
        type="button"
        onClick={() => onOpenQuote(source.quoteId)}
        className="font-bold text-blue-600 hover:underline focus:underline outline-none"
        title="Angebot öffnen"
      >
        {source.quoteNumber}
      </button>
    ) : (
      <span className="font-bold text-slate-700">{source.quoteNumber}</span>
    )}
    {source.orderNumber && <> · Auftrag <span className="font-bold text-slate-700">{source.orderNumber}</span></>}
    {' · '}angenommen am {fmtDateDmy(source.acceptedAt)}
    {source.customerRef && <> · Bestell-Nr. {source.customerRef}</>}
  </span>
);

/**
 * Vereinbarte Preise eines Rahmenvertrags — NUR LESBAR. Sie sind eingefroren
 * wie das Angebot, aus dem sie stammen: ein Bearbeiten hier liesse Vertrag und
 * Angebot auseinanderlaufen. Standard zugeklappt, die Liste kann lang sein.
 */
const FrameworkItemsTable: React.FC<{ contract: FrameworkContract }> = ({ contract }) => {
  const [open, setOpen] = useState(false);
  const items = contract.items || [];
  if (items.length === 0) return null;
  const discount = Number(contract.discountPercent) || 0;
  return (
    <div className="pt-1">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-700"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        {open ? 'Preispositionen ausblenden' : `${items.length} Preispositionen anzeigen`}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          <div className="overflow-x-auto bg-white rounded-xl border border-slate-200">
            <table className="w-full text-left border-collapse min-w-[560px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                  <th className="px-3 py-2 w-[70px]">Pos</th>
                  <th className="px-3 py-2">Bezeichnung</th>
                  <th className="px-3 py-2 w-[70px]">Einheit</th>
                  <th className="px-3 py-2 text-right w-[95px]">EP</th>
                  <th className="px-3 py-2 text-right w-[70px]">Rabatt</th>
                  <th className="px-3 py-2 text-right w-[100px]">EP netto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it, idx) => {
                  // Sektionsueberschrift nur, wo sie wechselt — wie im Angebot.
                  const section = it.sectionTitle && it.sectionTitle !== items[idx - 1]?.sectionTitle
                    ? it.sectionTitle
                    : null;
                  return (
                    <React.Fragment key={it.id}>
                      {section && (
                        <tr className="bg-slate-50/70">
                          <td colSpan={6} className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">{section}</td>
                        </tr>
                      )}
                      <tr>
                        <td className="px-3 py-2 text-xs font-mono text-slate-400 align-top whitespace-nowrap">{it.posNumber || it.lvPosition || '—'}</td>
                        <td className="px-3 py-2 text-sm text-slate-700 align-top">
                          {it.title}
                          {it.nep && (
                            <span
                              className="ml-1.5 inline-block px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-[10px] font-bold text-amber-700 align-middle"
                              title="Eventualposition: Ausführung nur auf gesonderte Anforderung"
                            >
                              NEP
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500 align-top">{it.unit}</td>
                        <td className="px-3 py-2 text-sm text-slate-500 text-right align-top whitespace-nowrap">{fmtEur(it.unitPrice)}</td>
                        <td className="px-3 py-2 text-sm text-slate-500 text-right align-top whitespace-nowrap">
                          {it.discountPercent ? `${fmtPercent(it.discountPercent)} %` : '—'}
                        </td>
                        <td className="px-3 py-2 text-sm font-bold text-slate-800 text-right align-top whitespace-nowrap">
                          {fmtEur(frameworkItemNetUnitPrice(it, discount))}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {discount > 0 && (
            <p className="text-xs text-slate-500">
              Alle Preise netto, inkl. {fmtPercent(discount)} % Gesamtrabatt des Angebots.
            </p>
          )}
          <p className="text-xs text-slate-400">Nur lesbar — Preisänderungen über ein neues Angebot.</p>
        </div>
      )}
    </div>
  );
};

// Status-Badge fuer die Historie – gespiegelt aus QuotesView (STATUS_META).
const QUOTE_STATUS_META: Record<QuoteStatus, { label: string; cls: string }> = {
  draft: { label: 'Entwurf', cls: 'bg-slate-100 text-slate-600' },
  sent: { label: 'Versendet', cls: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Angenommen', cls: 'bg-emerald-100 text-emerald-700' },
  partial: { label: 'Teilbeauftragt', cls: 'bg-teal-100 text-teal-700' },
  rejected: { label: 'Abgelehnt', cls: 'bg-red-100 text-red-700' },
  expired: { label: 'Abgelaufen', cls: 'bg-amber-100 text-amber-700' },
};
const QuoteStatusBadge: React.FC<{ status: QuoteStatus }> = ({ status }) => {
  const m = QUOTE_STATUS_META[status] || QUOTE_STATUS_META.draft;
  return <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold ${m.cls}`}>{m.label}</span>;
};

// Kundentyp-Umschalter. Fehlender Wert = Unternehmen (Altbestand), darum
// wird beim Lesen immer auf 'company' zurueckgefallen.
const CUSTOMER_TYPES: { key: NonNullable<Customer['customerType']>; label: string; icon: React.ReactNode }[] = [
  { key: 'company', label: 'Unternehmen', icon: <Building2 className="w-4 h-4" /> },
  { key: 'person', label: 'Privatperson', icon: <User className="w-4 h-4" /> },
];

const mapCatalogRow = (d: any): ServiceItem => ({
  id: d.id,
  lvPosition: d.lv_position,
  title: d.title,
  unit: d.unit,
  unitPrice: d.unit_price,
  category: d.category,
  isDefault: d.is_default,
  assignment: d.assignment || 'none',
  quantityContracted: 1,
  quantityUsed: 0,
  status: 'open',
});

interface Props {
  customer: Customer;
  onChange: (updated: Customer) => void;
  onBack: () => void;
  quoteConfig?: QuoteConfig;
  /**
   * Grundeinstellung des Rechnungsmoduls. Nur fuer den Platzhalter im
   * Zahlungsziel-Feld: ein leeres Feld soll zeigen, was stattdessen gilt.
   */
  defaultPaymentTermsDays?: number;
  /** Reiter beim Oeffnen — die Rueckkehr aus einem Beleg landet in der Historie. */
  initialTab?: CustomerDetailTab;
  /**
   * Spruenge aus der Historie in Angebot, Rechnung oder Projekt. Fehlt einer,
   * bleiben die Zeilen dieser Tabelle ohne Link.
   */
  onOpenQuote?: (quoteId: string) => void;
  onOpenInvoice?: (invoiceId: string) => void;
  onOpenProject?: (project: { id: string; name: string; status: string }) => void;
  /**
   * Nur lesen (Rollensystem): ohne „Kunden verwalten“ — etwa wer nur Angebote
   * schreibt — kein Autosave, keine Sonderpreise, Eingaben gesperrt. Die
   * Historie und ihre Spruenge bleiben. Die Datenbank prueft dasselbe (Nr. 152).
   */
  readOnly?: boolean;
}

export type CustomerDetailTab = 'stammdaten' | 'konditionen' | 'bewertung' | 'preise' | 'historie';
type TabKey = CustomerDetailTab;

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'stammdaten', label: 'Stammdaten', icon: <Building2 className="w-4 h-4" /> },
  { key: 'konditionen', label: 'Konditionen', icon: <Sliders className="w-4 h-4" /> },
  { key: 'bewertung', label: 'Bewertung', icon: <Star className="w-4 h-4" /> },
  { key: 'preise', label: 'Preise', icon: <Tag className="w-4 h-4" /> },
  { key: 'historie', label: 'Historie', icon: <History className="w-4 h-4" /> },
];

const inputClass =
  'w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all';
const labelClass = 'text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1';

// Historie: Zeile oeffnet den Beleg bzw. das Projekt, das PDF hat eine eigene Spalte.
const historyRowClass = (linked: boolean) =>
  linked ? 'group cursor-pointer hover:bg-blue-50/40 transition-colors' : 'hover:bg-slate-50/60';
const historyLinkClass = 'text-blue-600 group-hover:underline focus:underline outline-none';
const historyPdfClass =
  'inline-flex items-center justify-center p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-60 transition-colors';

export const CustomerDetail: React.FC<Props> = ({
  customer, onChange, onBack, quoteConfig, defaultPaymentTermsDays,
  initialTab, onOpenQuote, onOpenInvoice, onOpenProject, readOnly = false,
}) => {
  // quoteConfig wird bewusst durchgereicht (App → CrmView → CustomerDetail),
  // aktuell hier aber ungenutzt.
  void quoteConfig;
  // Stichtag fuer die Faelligkeitsanzeige der Belege — einmal je Render, nicht
  // je Tabellenzeile. Ortsdatum, nicht UTC (localToday).
  const today = localToday();
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab ?? 'stammdaten');
  // Lokaler Entwurf – wird erst beim Speichern persistiert.
  const [draft, setDraft] = useState<Customer>(customer);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Draft-Stand, dessen Autosave fehlschlug — verhindert Alert-Schleifen.
  const autosaveFailedForRef = useRef<Customer | null>(null);

  // Adress-Verifikation (Nominatim, non-blocking).
  const [addrChecking, setAddrChecking] = useState(false);
  const [addrCheck, setAddrCheck] = useState<{ ok: boolean; text: string } | null>(null);

  // Verortung als Klartext: Koordinaten + Guete + Hinweis auf manuelle Pflege.
  // '' bedeutet "noch nicht verortet" (der Normalfall vor dem Sammellauf).
  const geoText = useMemo(() => {
    const coords = formatCoordinates(customer.lat, customer.lng);
    if (!coords) return '';
    const teile = [coords, geoQualityLabel(customer.geoQuality), customer.geoManual ? 'manuell' : ''];
    return teile.filter(Boolean).join(' · ');
  }, [customer.lat, customer.lng, customer.geoQuality, customer.geoManual]);

  // Preise-Tab: Katalog + Kundenpreise
  const [catalog, setCatalog] = useState<ServiceItem[]>([]);
  const [prices, setPrices] = useState<CustomerPrice[]>([]);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [pricesLoaded, setPricesLoaded] = useState(false);
  // Bearbeitungspuffer je Katalog-Position (Strings, damit Leerfeld = kein Sonderpreis)
  const [edits, setEdits] = useState<Record<string, { unitPrice: string; discountPercent: string }>>({});
  const [savingRow, setSavingRow] = useState<string | null>(null);

  // Rahmenvertrags-Vorlagen = LV-Vorlagen aus dem Katalog (service_templates).
  // Ein Rahmenvertrag verweist per templateId auf eine solche Vorlage. Lazy
  // beim Öffnen der Konditionen, fehlertolerant (Vorlagen sind optional).
  const [serviceTemplates, setServiceTemplates] = useState<{ id: string; name: string }[]>([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const templateNameById = useMemo(() => {
    const m: Record<string, string> = {};
    serviceTemplates.forEach((t) => { m[t.id] = t.name; });
    return m;
  }, [serviceTemplates]);
  // Vertrag, dessen Entfernen gerade in der Zeile bestaetigt werden muss.
  const [confirmRemoveFrameworkId, setConfirmRemoveFrameworkId] = useState<string | null>(null);

  // Historie-Tab: Angebote + Projekte des Kunden (lazy, read-only).
  const [histQuotes, setHistQuotes] = useState<Quote[]>([]);
  const [histProjects, setHistProjects] = useState<Array<{ id: string; name: string; projectNumber: string; status: string; createdAt: string }>>([]);
  const [histInvoices, setHistInvoices] = useState<Invoice[]>([]);
  /** id -> Kundennummer/Name gemergter Quellkunden, fuer die Herkunftsspalte. */
  const [mergedSourceNames, setMergedSourceNames] = useState<Map<string, string>>(new Map());
  const [histLoading, setHistLoading] = useState(false);
  const [histLoaded, setHistLoaded] = useState(false);
  const [histError, setHistError] = useState<string | null>(null);

  const loadHistory = async () => {
    setHistLoading(true);
    setHistError(null);
    try {
      const [qs, ps, allCust] = await Promise.all([
        fetchQuotesForCustomer(customer.id),
        fetchProjectsForCustomer(customer.id),
        fetchCustomers({ includeArchived: true }),
      ]);
      // Belege gemergter Quellkunden gehoeren mit in die Akte: ihre
      // Rechnungen bleiben beim Merge am alten Kunden (GoBD), fachlich sind
      // sie aber Umsatz DIESER Firma. Ketten werden dabei bis zum Ende
      // verfolgt (A -> B -> C liefert in der Akte von C auch A) — genau wie
      // beim Umsatz-KPI, der ueber dieselbe Aufloesung faltet.
      const mergedIds = new Set(collectMergedSourceIds(allCust, customer.id));
      const mergedSources = allCust.filter((c) => mergedIds.has(c.id));
      setMergedSourceNames(new Map(mergedSources.map((c) => [c.id, c.customerNumber || c.name])));
      const invs = await fetchInvoicesForCustomer([
        customer.id,
        ...mergedSources.map((c) => c.id),
      ]);
      setHistQuotes(qs);
      setHistProjects(ps);
      setHistInvoices(invs);
    } catch (e) {
      setHistError(e instanceof Error ? e.message : 'Historie konnte nicht geladen werden.');
    } finally {
      setHistLoading(false);
      setHistLoaded(true);
    }
  };

  // Archiviertes Rechnungs-PDF oeffnen. pdfUrl ist hier ein Pfad im privaten
  // Bucket, kein Link — wie im Rechnungseditor erst signieren.
  const [openingInvoiceId, setOpeningInvoiceId] = useState<string | null>(null);
  const [invoicePdfError, setInvoicePdfError] = useState<string | null>(null);
  const openInvoicePdf = async (inv: Invoice) => {
    if (!inv.pdfUrl) return;
    setInvoicePdfError(null);
    // Browser: Tab SOFORT im Klick oeffnen, erst nach dem Signieren umlenken.
    // Ein window.open nach dem await hat keine Benutzeraktivierung mehr und
    // wird vom Popup-Blocker still verschluckt (Safari, langsame Antwort).
    // Android-App: die WebView kennt keine leeren Fenster — dort reicht
    // nativeShims die fertige URL an den System-Browser, ohne Blocker.
    const tab = istNativeApp() ? null : window.open('', '_blank');
    if (!istNativeApp() && !tab) {
      setInvoicePdfError('Der Browser hat den neuen Tab blockiert. Bitte Pop-ups für diese Seite erlauben.');
      return;
    }
    if (tab) {
      // about:blank ist same-origin: opener kappen, bevor das PDF darin laedt.
      tab.opener = null;
      tab.document.title = 'PDF wird geladen…';
    }
    setOpeningInvoiceId(inv.id);
    try {
      const url = await getInvoicePdfUrl(inv.pdfUrl);
      if (tab) tab.location.href = url;
      else window.open(url, '_blank', 'noopener');
    } catch (e) {
      tab?.close();
      setInvoicePdfError(`PDF zu ${inv.invoiceNumber || 'diesem Beleg'} konnte nicht geöffnet werden: ${describeError(e)}`);
    } finally {
      setOpeningInvoiceId(null);
    }
  };

  // Lazy: erst beim Oeffnen des Historie-Tabs laden.
  useEffect(() => {
    if (activeTab === 'historie' && !histLoaded && !histLoading) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const priceMap = useMemo(() => {
    const m: Record<string, CustomerPrice> = {};
    prices.forEach((p) => { m[p.catalogItemId] = p; });
    return m;
  }, [prices]);

  const buildEdits = (cat: ServiceItem[], ps: CustomerPrice[]) => {
    const m: Record<string, CustomerPrice> = {};
    ps.forEach((p) => { m[p.catalogItemId] = p; });
    const next: Record<string, { unitPrice: string; discountPercent: string }> = {};
    cat.forEach((it) => {
      const p = m[it.id];
      next[it.id] = {
        unitPrice: p?.unitPrice != null ? String(p.unitPrice) : '',
        discountPercent: p?.discountPercent != null ? String(p.discountPercent) : '',
      };
    });
    setEdits(next);
  };

  const loadPrices = async () => {
    setPricesLoading(true);
    try {
      const { data } = await supabase.from('service_catalog').select('*').order('lv_position');
      const cat = (data || []).map(mapCatalogRow);
      const ps = await fetchCustomerPrices(customer.id);
      setCatalog(cat);
      setPrices(ps);
      buildEdits(cat, ps);
    } catch (e) {
      alert('Preise konnten nicht geladen werden: ' + describeError(e));
    } finally {
      setPricesLoading(false);
      setPricesLoaded(true);
    }
  };

  // Lazy: erst beim Öffnen des Preise-Tabs laden.
  useEffect(() => {
    if (activeTab === 'preise' && !pricesLoaded && !pricesLoading) loadPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // LV-Vorlagen aus dem Katalog (nur id + name) fuer die Rahmenvertrags-Auswahl.
  const loadServiceTemplates = async () => {
    try {
      const { data } = await supabase.from('service_templates').select('id, name').order('name');
      setServiceTemplates(((data as { id: string; name: string }[]) || []));
    } catch {
      /* Vorlagen optional – ohne Katalog-Vorlagen bleibt nur der manuelle Weg. */
    } finally {
      setTemplatesLoaded(true);
    }
  };

  // Lazy: erst beim Öffnen des Konditionen-Tabs laden.
  useEffect(() => {
    if (activeTab === 'konditionen' && !templatesLoaded) loadServiceTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const setEdit = (catId: string, field: 'unitPrice' | 'discountPercent', val: string) => {
    setEdits((e) => ({ ...e, [catId]: { ...e[catId], [field]: val } }));
  };

  const persistRow = async (catId: string) => {
    if (readOnly) return;
    const e = edits[catId] || { unitPrice: '', discountPercent: '' };
    const unitPrice = e.unitPrice.trim() === '' ? undefined : Number(e.unitPrice);
    const discountPercent = e.discountPercent.trim() === '' ? undefined : Number(e.discountPercent);
    const existing = priceMap[catId];
    // Beide leer -> vorhandenen Sonderpreis entfernen bzw. nichts tun.
    if (unitPrice === undefined && discountPercent === undefined) {
      if (existing) await resetRow(catId);
      return;
    }
    // Keine Änderung -> Persistieren sparen.
    if (existing && existing.unitPrice === unitPrice && existing.discountPercent === discountPercent) return;
    setSavingRow(catId);
    try {
      await upsertCustomerPrice({
        id: existing?.id,
        customerId: customer.id,
        catalogItemId: catId,
        unitPrice,
        discountPercent,
        note: existing?.note,
      });
      const ps = await fetchCustomerPrices(customer.id);
      setPrices(ps);
    } catch (err) {
      alert('Speichern fehlgeschlagen: ' + rechtFehlerOder(err));
    } finally {
      setSavingRow(null);
    }
  };

  const resetRow = async (catId: string) => {
    if (readOnly) return;
    const existing = priceMap[catId];
    setEdit(catId, 'unitPrice', '');
    setEdit(catId, 'discountPercent', '');
    if (!existing) return;
    setSavingRow(catId);
    try {
      await deleteCustomerPrice(existing.id);
      setPrices((prev) => prev.filter((p) => p.id !== existing.id));
    } catch (err) {
      alert('Zurücksetzen fehlgeschlagen: ' + rechtFehlerOder(err));
    } finally {
      setSavingRow(null);
    }
  };

  const set = <K extends keyof Customer>(key: K, val: Customer[K]) => {
    setDraft((d) => ({ ...d, [key]: val }));
    setSaved(false);
  };

  const customerType = draft.customerType ?? 'company';
  const isPerson = customerType === 'person';

  /**
   * Anzeigewerte fuer Vor-/Nachname. Sind die Felder leer (Altbestand oder
   * frisch auf "Privatperson" umgestellt), werden sie AUS DEM NAMEN abgeleitet
   * — abgeleitet, nicht gespeichert: solange niemand tippt, wird nichts
   * geaendert und kein Autosave ausgeloest.
   */
  const personParts = useMemo(
    () => personNameParts({ firstName: draft.firstName, lastName: draft.lastName, name: draft.name }),
    [draft.firstName, draft.lastName, draft.name],
  );

  /**
   * Namensteil setzen und den Vollnamen mitfuehren: `name` bleibt ueberall die
   * Anzeige (PDF, Angebot, Rechnung, Suche) — im Personen-Modus wird das Feld
   * gar nicht mehr angezeigt, es MUSS also hier entstehen. Der Titel gehoert
   * nicht hinein.
   */
  const setPersonNamePart = (key: 'firstName' | 'lastName', val: string) => {
    setDraft((d) => withPersonNamePart(d, key, val));
    setSaved(false);
  };

  /**
   * Kundentyp umschalten. Beim Wechsel auf "Privatperson" wird ein vorhandener
   * Name EINMALIG in Vor-/Nachname vorbelegt (samt Anrede/Titel, aber nur in
   * leere Felder) — ein Klick soll Arbeit abnehmen, nicht Eingaben ueberschreiben.
   */
  const setCustomerType = (key: NonNullable<Customer['customerType']>) => {
    if (key !== 'person') {
      set('customerType', key);
      return;
    }
    setDraft((d) => {
      const next: Customer = { ...d, customerType: 'person' };
      const hasParts = (d.firstName ?? '').trim() || (d.lastName ?? '').trim();
      const raw = (d.name ?? '').trim();
      if (!hasParts && raw) {
        const parsed = parsePersonName(raw);
        next.firstName = parsed.firstName;
        next.lastName = parsed.lastName;
        if (!(d.salutation ?? '').trim() && parsed.salutation) next.salutation = parsed.salutation;
        if (!(d.title ?? '').trim() && parsed.title) next.title = parsed.title;
        next.name = composePersonName(next.firstName, next.lastName) || raw;
      }
      return next;
    });
    setSaved(false);
  };

  // Einzelbewertung setzen; Gesamtbewertung = Durchschnitt der gesetzten
  // Einzelbewertungen (1 Dezimale), automatisch mitgefuehrt.
  const setRating = (
    key: 'ratingZahlungsmoral' | 'ratingZusammenarbeit' | 'ratingWirtschaftlichkeit',
    val: number,
  ) => {
    setDraft((d) => {
      const next = { ...d, [key]: val };
      const vals = [next.ratingZahlungsmoral, next.ratingZusammenarbeit, next.ratingWirtschaftlichkeit]
        .filter((v): v is number => typeof v === 'number' && v > 0);
      next.ratingOverall = vals.length
        ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10
        : undefined;
      return next;
    });
    setSaved(false);
  };

  // Kontakte
  const addContact = () => {
    const contact: CustomerContact = { id: crypto.randomUUID(), name: '' };
    set('contacts', [...(draft.contacts || []), contact]);
  };
  const updateContact = (id: string, field: keyof CustomerContact, val: string) => {
    set('contacts', (draft.contacts || []).map((c) => (c.id === id ? { ...c, [field]: val } : c)));
  };
  const removeContact = (id: string) => {
    set('contacts', (draft.contacts || []).filter((c) => c.id !== id));
  };

  // Rahmenvertraege. Ein Rahmenvertrag IST eine LV-Vorlage aus dem Katalog:
  // per template angelegt uebernimmt er Bezeichnung (name) und Verweis
  // (templateId); die Vertragsnummer bleibt kundenspezifisch. Ohne template
  // entsteht ein manueller Rahmenvertrag ohne Katalog-Bezug.
  const addFrameworkContract = (template?: { id: string; name: string }) => {
    const fc: FrameworkContract = template
      ? { id: crypto.randomUUID(), number: '', title: template.name, templateId: template.id }
      : { id: crypto.randomUUID(), number: '' };
    set('frameworkContracts', [...(draft.frameworkContracts || []), fc]);
  };
  const updateFrameworkContract = (id: string, field: keyof FrameworkContract, val: string) => {
    set('frameworkContracts', (draft.frameworkContracts || []).map((f) => (f.id === id ? { ...f, [field]: val } : f)));
  };
  // Verknuepfung zur Katalog-Vorlage loesen (Bezeichnung/Nummer bleiben erhalten).
  const unlinkFrameworkTemplate = (id: string) => {
    set('frameworkContracts', (draft.frameworkContracts || []).map((f) => (f.id === id ? { ...f, templateId: undefined } : f)));
  };
  const removeFrameworkContract = (id: string) => {
    setConfirmRemoveFrameworkId(null);
    set('frameworkContracts', (draft.frameworkContracts || []).filter((f) => f.id !== id));
  };
  // Ein Vertrag mit vereinbarten Preisen geht mit einem Fehlklick sonst samt
  // Preisliste verloren (Autosave nach 2 s) — darum erst eine Rueckfrage in
  // der Zeile. Vertraege ohne Positionen wie bisher sofort.
  const requestRemoveFrameworkContract = (f: FrameworkContract) => {
    if ((f.items?.length ?? 0) > 0) setConfirmRemoveFrameworkId(f.id);
    else removeFrameworkContract(f.id);
  };
  // Genau ein Standard-Rahmenvertrag je Kunde: Auswahl setzt isDefault am Ziel
  // und entfernt es bei allen anderen. Persistiert mit dem regulaeren Speichern.
  const setDefaultFrameworkContract = (id: string) => {
    set('frameworkContracts', (draft.frameworkContracts || []).map((f) => ({ ...f, isDefault: f.id === id })));
  };

  // Alt-Adresse (customer.address) best-effort in Straße/PLZ/Ort uebernehmen.
  const applyLegacyAddress = () => {
    const parsed = parseLegacyAddress(customer.address || '');
    setDraft((d) => ({
      ...d,
      street: parsed.street ?? d.street,
      zip: parsed.zip ?? d.zip,
      city: parsed.city ?? d.city,
    }));
    setSaved(false);
  };

  // Adresse ueber Nominatim pruefen (informativ, blockiert das Speichern nicht).
  const handleVerifyAddress = async () => {
    setAddrChecking(true);
    setAddrCheck(null);
    try {
      const found = await verifyAddress({ street: draft.street, zip: draft.zip, city: draft.city });
      setAddrCheck(found ? { ok: true, text: found } : { ok: false, text: 'Nicht eindeutig gefunden' });
    } catch {
      setAddrCheck({ ok: false, text: 'Nicht eindeutig gefunden' });
    } finally {
      setAddrChecking(false);
    }
  };

  const handleSave = async () => {
    if (readOnly) return;
    setSaving(true);
    setSaved(false);
    // Adress-Komposition (PDF-Fallback), Feld-Diff und Historien-Eintraege
    // liegen in customerQuickEdit.ts — dieselbe Regel benutzt die
    // Schnellbearbeitung aus Rechnung und Angebot, und nur dort ist sie
    // testbar. Zwei Kopien wuerden unbemerkt auseinanderlaufen.
    const { toSave, updates, changes: stammChanges } = planCustomerUpdate(customer, draft);
    // Stammdaten-Aenderungen (Name, Anschrift, Kontakt) historisieren: der
    // Eintrag entsteht beim Speichern, nicht beim Tippen — EIN Eintrag je
    // Speichervorgang, mit Alt- und Neuwert.
    if (stammChanges.length) {
      const { data: auth } = await supabase.auth.getUser();
      const newLog = appendChangeEntry(
        customer.changeLog ?? [],
        stammChanges,
        new Date().toISOString(),
        auth.user?.email ?? undefined,
      );
      updates.changeLog = newLog;
      toSave.changeLog = newLog;
    }
    try {
      if (updates.frameworkContracts !== undefined) {
        // Rahmenvertraege gehen als GANZE Liste raus: abgeglichen mit dem
        // Serverstand und verankert geschrieben, sonst loeschte eine aeltere
        // Akte einen inzwischen angehaengten Vertrag (Angebotsannahme).
        toSave.frameworkContracts = await updateCustomerMergingFrameworkContracts(
          toSave.id,
          updates,
          customer.frameworkContracts ?? [],
        );
      } else if (Object.keys(updates).length > 0) {
        await updateCustomer(toSave.id, updates);
      }
      // Eingaben, die WAEHREND des Speicherns passiert sind, nicht verwerfen —
      // aber die vom Server dazugekommenen Rahmenvertraege mitnehmen, sonst
      // hielte das naechste Speichern sie fuer in der Akte entfernt.
      setDraft((cur) => {
        if (cur === draft) return toSave;
        const curList = cur.frameworkContracts ?? [];
        const list = mergeFrameworkContractsOnSave(
          draft.frameworkContracts ?? [],
          curList,
          toSave.frameworkContracts ?? [],
        );
        return list === curList ? cur : { ...cur, frameworkContracts: list };
      });
      onChange(toSave); // optimistisches lokales Update
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      // Autosave fuer diesen Draft-Stand pausieren, sonst Alert-Schleife alle 2s.
      autosaveFailedForRef.current = draft;
      alert('Speichern fehlgeschlagen: ' + rechtFehlerOder(e));
    } finally {
      setSaving(false);
    }
  };

  // ── Autosave: 2s nach der letzten Aenderung ────────────────────────────────
  // dirty = Draft-Referenz weicht vom Parent-Prop ab (jede Mutation erzeugt ein
  // neues Objekt; das Save-Echo via onChange stellt Referenzgleichheit wieder
  // her). Nach Fehler erst wieder versuchen, wenn sich der Draft geaendert hat.
  useEffect(() => {
    if (readOnly || saving || draft === customer) return;
    if (autosaveFailedForRef.current === draft) return;
    const t = setTimeout(() => { void handleSave(); }, 2000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, customer, saving, readOnly]);

  return (
    <div data-glass-view="crm" className="h-full overflow-y-auto">
    <div className="space-y-6 max-w-5xl mx-auto animate-fade-in p-4 md:p-6 pb-20">
      {/* Header */}
      <div data-glass="kopf" className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
            title="Zurück zur Liste"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl">
            {isPerson ? <User className="w-7 h-7" /> : <Building2 className="w-7 h-7" />}
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">{draft.name || 'Kunde'}</h2>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">
              {draft.customerNumber ? `Kunden-Nr. ${draft.customerNumber}` : 'Kundendetails'}
            </p>
          </div>
        </div>
        {readOnly ? (
          <span
            className="px-3 py-1.5 rounded-xl bg-slate-100 border border-slate-200 text-xs font-bold text-slate-500"
            title={rechtFehltHinweis('kunden.verwalten')}
          >
            Nur-Ansicht
          </span>
        ) : (
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving}
            className="shadow-lg border-b-2 border-blue-700"
            icon={saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          >
            {saving ? 'Speichert…' : saved ? 'Gespeichert' : 'Speichern'}
          </Button>
        )}
      </div>

      {/* Tab-Leiste */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-bold whitespace-nowrap border-b-2 transition-all ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                  : 'border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-50'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6 sm:p-8">
          {/* Nur-Ansicht (readOnly): je Reiter sperrt ein fieldset die
              Eingaben. Was nur zeigt oder springt (Herkunft eines
              Rahmenvertrags, seine Preisliste, die Historie), bleibt
              ausserhalb und damit bedienbar. */}
          {/* STAMMDATEN */}
          {activeTab === 'stammdaten' && (
            <fieldset disabled={readOnly} className="m-0 border-0 p-0 space-y-6 min-w-0">
              {/* Kundentyp. Der Wechsel auf Unternehmen loescht Anrede/Titel
                  NICHT — er blendet sie nur aus; ein versehentlicher Klick
                  darf keine Eingaben vernichten. */}
              <div className="space-y-1.5">
                <label className={labelClass}>Kundentyp</label>
                <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden">
                  {CUSTOMER_TYPES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setCustomerType(t.key)}
                      aria-pressed={customerType === t.key}
                      className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold whitespace-nowrap border-b-2 transition-all ${
                        customerType === t.key
                          ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                          : 'border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {t.icon}
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {isPerson && (
                <div className="grid grid-cols-1 sm:grid-cols-[180px_180px] gap-4">
                  <div className="space-y-1.5">
                    <label className={labelClass}>Anrede</label>
                    <select value={draft.salutation || ''} onChange={(e) => set('salutation', e.target.value)} className={inputClass}>
                      <option value="">—</option>
                      <option value="Herr">Herr</option>
                      <option value="Frau">Frau</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className={labelClass}>Titel</label>
                    <input value={draft.title || ''} onChange={(e) => set('title', e.target.value)} className={inputClass} placeholder="Dr." />
                  </div>
                </div>
              )}

              {isPerson ? (
                /* Privatperson: Vor- und Nachname getrennt (DATEV-Export,
                   Briefanrede). Der Vollname entsteht daraus automatisch und
                   steht weiterhin in Kopfzeile, PDF und Suche. */
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_180px] gap-4">
                  <div className="space-y-1.5">
                    <label className={labelClass}>Vorname</label>
                    <div className="relative">
                      <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                      <input
                        value={personParts.firstName}
                        onChange={(e) => setPersonNamePart('firstName', e.target.value)}
                        className={`${inputClass} pl-10 font-bold`}
                        placeholder="Vorname"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className={labelClass}>Nachname *</label>
                    <input
                      value={personParts.lastName}
                      onChange={(e) => setPersonNamePart('lastName', e.target.value)}
                      className={`${inputClass} font-bold`}
                      placeholder="Nachname"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className={labelClass}>Kundennummer</label>
                    <input value={draft.customerNumber || ''} onChange={(e) => set('customerNumber', e.target.value)} className={`${inputClass} font-mono`} placeholder="z. B. 30234" />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-4">
                  <div className="space-y-1.5">
                    <label className={labelClass}>Name 1 (Zeile 1) *</label>
                    <div className="relative">
                      <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                      <input value={draft.name} onChange={(e) => set('name', e.target.value)} className={`${inputClass} pl-10 font-bold`} placeholder="Firmenname" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className={labelClass}>Kundennummer</label>
                    <input value={draft.customerNumber || ''} onChange={(e) => set('customerNumber', e.target.value)} className={`${inputClass} font-mono`} placeholder="z. B. 30234" />
                  </div>
                </div>
              )}

              {/* Namenszeilen 2 und 3: nur Adress-Andruck auf Rechnung und
                  Angebot. Suche, Listen und DATEV kennen weiterhin nur name. */}
              {!isPerson && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className={labelClass}>Name 2 (Zeile 2)</label>
                    <input value={draft.name2 || ''} onChange={(e) => set('name2', e.target.value)} className={inputClass} placeholder="z. B. Geschäftsbereich / Abteilung" />
                  </div>
                  <div className="space-y-1.5">
                    <label className={labelClass}>Name 3 (Zeile 3)</label>
                    <input value={draft.name3 || ''} onChange={(e) => set('name3', e.target.value)} className={inputClass} placeholder="z. B. z. Hd. …" />
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <label className={labelClass}>Adresse</label>
                  <Button
                    variant="secondary"
                    onClick={handleVerifyAddress}
                    disabled={addrChecking || (!draft.street && !draft.zip && !draft.city)}
                    icon={addrChecking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                    className="h-8 text-xs"
                  >
                    Adresse prüfen
                  </Button>
                </div>

                {/* Alt-Adresse (Legacy) – nur wenn gesetzt und neue Felder leer. */}
                {customer.address && !draft.street && !draft.zip && !draft.city && (
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-500">
                    <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-400" />
                    <span className="flex-1">Alt: {customer.address}</span>
                    <button
                      type="button"
                      onClick={applyLegacyAddress}
                      className="shrink-0 px-2.5 py-1 rounded-lg bg-white border border-slate-200 font-bold text-slate-600 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-all"
                    >
                      Übernehmen
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_2fr] gap-4">
                  <div className="space-y-1.5">
                    <label className={labelClass}>Straße & Nr.</label>
                    <div className="relative">
                      <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                      <input value={draft.street || ''} onChange={(e) => { set('street', e.target.value); setAddrCheck(null); }} className={`${inputClass} pl-10`} placeholder="Musterstraße 1" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className={labelClass}>PLZ</label>
                    <input value={draft.zip || ''} onChange={(e) => { set('zip', e.target.value); setAddrCheck(null); }} className={inputClass} placeholder="12345" />
                  </div>
                  <div className="space-y-1.5">
                    <label className={labelClass}>Ort</label>
                    <input value={draft.city || ''} onChange={(e) => { set('city', e.target.value); setAddrCheck(null); }} className={inputClass} placeholder="Musterstadt" />
                  </div>
                </div>

                {addrCheck && (
                  <div className={`flex items-start gap-2 p-3 rounded-xl border text-sm ${
                    addrCheck.ok
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : 'bg-amber-50 border-amber-200 text-amber-700'
                  }`}>
                    {addrCheck.ok ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
                    <span>{addrCheck.ok ? `Adresse gefunden: ${addrCheck.text}` : addrCheck.text}</span>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className={labelClass}>Branche</label>
                  <select value={draft.branche || ''} onChange={(e) => set('branche', e.target.value)} className={inputClass}>
                    <option value="">— ohne Branche —</option>
                    {BRANCHE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                {/* Verortung: reine Anzeige. Gesetzt wird sie vom Sammellauf
                    "Adressen verorten" in der Kundenliste — beim Tippen zu
                    geocodieren verbietet das Nominatim-Limit. */}
                <div className="space-y-1.5">
                  <label className={labelClass}>Verortung</label>
                  <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-500">
                    <MapPin className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                    {geoText ? (
                      <span className="font-mono text-slate-600">{geoText}</span>
                    ) : (
                      <span>Noch nicht verortet — Sammellauf „Adressen verorten“ in der Kundenliste</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <label className={labelClass}>E-Mail</label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                    <input type="email" value={draft.email || ''} onChange={(e) => set('email', e.target.value)} className={`${inputClass} pl-10`} placeholder="info@firma.de" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className={labelClass}>Telefon</label>
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                    <input value={draft.phone || ''} onChange={(e) => set('phone', e.target.value)} className={`${inputClass} pl-10`} placeholder="+49 …" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className={labelClass}>Website</label>
                  <div className="relative">
                    <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                    <input value={draft.website || ''} onChange={(e) => set('website', e.target.value)} className={`${inputClass} pl-10`} placeholder="www.firma.de" />
                  </div>
                </div>
              </div>

              {/* Kontakte */}
              <div className="pt-4 border-t border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-bold text-slate-700">Ansprechpartner</span>
                    <span className="text-xs text-slate-400">({(draft.contacts || []).length})</span>
                  </div>
                  <Button variant="secondary" onClick={addContact} icon={<Plus className="w-4 h-4" />} className="h-9 text-xs">
                    Kontakt hinzufügen
                  </Button>
                </div>

                {(draft.contacts || []).length === 0 ? (
                  <div className="py-8 text-center text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-100">
                    <User className="w-8 h-8 opacity-20 mx-auto mb-2" />
                    <p className="text-xs font-medium">Noch keine Ansprechpartner erfasst</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(draft.contacts || []).map((c) => (
                      <div key={c.id} className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr_1.5fr_1fr_auto] gap-2 items-center bg-slate-50 p-3 rounded-2xl border border-slate-100">
                        <input value={c.name} onChange={(e) => updateContact(c.id, 'name', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Name" />
                        <input value={c.role || ''} onChange={(e) => updateContact(c.id, 'role', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Funktion" />
                        <input value={c.email || ''} onChange={(e) => updateContact(c.id, 'email', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="E-Mail" />
                        <input value={c.phone || ''} onChange={(e) => updateContact(c.id, 'phone', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Telefon" />
                        <button onClick={() => removeContact(c.id)} className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all justify-self-end" title="Kontakt entfernen">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </fieldset>
          )}

          {/* KONDITIONEN */}
          {activeTab === 'konditionen' && (
            <div className="space-y-6 max-w-2xl">
              <fieldset disabled={readOnly} className="m-0 border-0 p-0 min-w-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
                  <div className="space-y-1.5">
                    <label className={labelClass}>Zahlungsziel (Tage)</label>
                    {/*
                      Leer heisst "Grundeinstellung", nicht 0. Frueher schrieb
                      ein leeres Feld die 0 und die Anlage belegte mit 14/30 vor
                      — dadurch trug jeder Kunde eine Kondition, und die globale
                      Einstellung erreichte keine Rechnung. Nur eine Abweichung
                      gehoert hier hinein.
                    */}
                    <input
                      type="number"
                      min={0}
                      value={
                        typeof draft.paymentTermsDays === 'number' && draft.paymentTermsDays > 0
                          ? draft.paymentTermsDays
                          : ''
                      }
                      placeholder={`Standard (${defaultPaymentTermsDays ?? DEFAULT_INVOICE_CONFIG.defaultPaymentTermsDays} Tage)`}
                      onChange={(e) =>
                        set(
                          'paymentTermsDays',
                          e.target.value === '' ? null : Number(e.target.value) || null,
                        )
                      }
                      className={inputClass}
                    />
                    <p className="text-xs text-slate-500">
                      Leer lassen, wenn die Grundeinstellung gilt.
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <label className={labelClass}>Standardrabatt (%)</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      value={Number.isFinite(draft.defaultDiscountPercent) ? draft.defaultDiscountPercent : 0}
                      onChange={(e) => set('defaultDiscountPercent', e.target.value === '' ? 0 : Number(e.target.value))}
                      className={inputClass}
                    />
                  </div>
                  {/* Skonto: 0/0 bedeutet "kein Skonto" und wird beim Speichern als NULL abgelegt */}
                  <div className="space-y-1.5">
                    <label className={labelClass}>Skonto (%)</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.1}
                      value={draft.skonto?.percent ?? 0}
                      onChange={(e) => set('skonto', { percent: e.target.value === '' ? 0 : Number(e.target.value), days: draft.skonto?.days ?? 0 })}
                      className={inputClass}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className={labelClass}>Skonto-Frist (Tage)</label>
                    <input
                      type="number"
                      min={0}
                      value={draft.skonto?.days ?? 0}
                      onChange={(e) => set('skonto', { percent: draft.skonto?.percent ?? 0, days: e.target.value === '' ? 0 : Number(e.target.value) })}
                      className={inputClass}
                    />
                  </div>
                </div>
              </fieldset>

              <p className="text-xs text-slate-400">Skonto wird auf neuen Rechnungen dieses Kunden als Zahlungsbedingung mit ausgerechnetem Skonto-Datum vorbelegt. 0 = kein Skonto.</p>

              {/* Rahmenvertraege */}
              <div className="pt-4 border-t border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-bold text-slate-700">Rahmenverträge</span>
                    <span className="text-xs text-slate-400">({(draft.frameworkContracts || []).length})</span>
                  </div>
                  {!readOnly && (
                    <div className="flex items-center gap-2">
                      {serviceTemplates.length > 0 && (
                        <select
                          value=""
                          onChange={(e) => {
                            const t = serviceTemplates.find((x) => x.id === e.target.value);
                            if (t) addFrameworkContract(t);
                            e.target.value = '';
                          }}
                          className="h-9 px-3 bg-white border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500/20"
                          title="Rahmenvertrag aus einer LV-Vorlage des Katalogs anlegen"
                        >
                          <option value="">Vorlage aus Katalog wählen…</option>
                          {serviceTemplates.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      )}
                      <Button variant="secondary" onClick={() => addFrameworkContract()} icon={<Plus className="w-4 h-4" />} className="h-9 text-xs">
                        Manuell hinzufügen
                      </Button>
                    </div>
                  )}
                </div>

                {(draft.frameworkContracts || []).length === 0 ? (
                  <div className="py-8 text-center text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-100">
                    <FileText className="w-8 h-8 opacity-20 mx-auto mb-2" />
                    <p className="text-xs font-medium">Noch keine Rahmenverträge erfasst</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(draft.frameworkContracts || []).map((f) => {
                      const validity = frameworkContractValidity(f, today);
                      const itemCount = f.items?.length ?? 0;
                      return (
                        <div key={f.id} className="bg-slate-50 p-3 rounded-2xl border border-slate-100 space-y-2">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-bold ${FRAMEWORK_VALIDITY_CLS[validity]}`}>
                              {FRAMEWORK_VALIDITY_LABELS[validity]}
                            </span>
                            {f.source && <FrameworkSourceLine source={f.source} onOpenQuote={onOpenQuote} />}
                          </div>
                          <fieldset disabled={readOnly} className="m-0 border-0 p-0 space-y-2 min-w-0">
                            <div className="grid grid-cols-1 md:grid-cols-[1fr_1.5fr_auto] gap-2 items-center">
                              <input value={f.number} onChange={(e) => updateFrameworkContract(f.id, 'number', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Nummer *" />
                              <div className="space-y-1.5">
                                {f.templateId && (
                                  <span className="inline-flex items-center gap-1.5 max-w-full px-2 py-1 bg-blue-50 border border-blue-200 rounded-lg text-[11px] font-bold text-blue-700" title="Verknüpfte LV-Vorlage aus dem Katalog">
                                    <Link2 className="w-3 h-3 shrink-0" />
                                    <span className="truncate">{templateNameById[f.templateId] || f.title || 'Katalog-Vorlage'}</span>
                                    <button type="button" onClick={() => unlinkFrameworkTemplate(f.id)} className="shrink-0 text-blue-400 hover:text-red-500" title="Verknüpfung zur Vorlage lösen">
                                      <X className="w-3 h-3" />
                                    </button>
                                  </span>
                                )}
                                <input value={f.title || ''} onChange={(e) => updateFrameworkContract(f.id, 'title', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Bezeichnung" />
                              </div>
                              {confirmRemoveFrameworkId === f.id ? (
                                <div className="flex items-center gap-1.5 justify-self-end text-xs" role="group" aria-label="Entfernen bestätigen">
                                  <span className="font-bold text-red-600 whitespace-nowrap">Wirklich entfernen?</span>
                                  <button
                                    type="button"
                                    onClick={() => removeFrameworkContract(f.id)}
                                    className="px-2.5 py-1 rounded-lg bg-red-600 text-white font-bold hover:bg-red-700 transition-colors"
                                  >
                                    Ja
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setConfirmRemoveFrameworkId(null)}
                                    className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-slate-600 font-bold hover:bg-slate-100 transition-colors"
                                  >
                                    Nein
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => requestRemoveFrameworkContract(f)}
                                  className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all justify-self-end"
                                  title={itemCount > 0 ? `Rahmenvertrag mit ${itemCount} Preispositionen entfernen` : 'Rahmenvertrag entfernen'}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                            {/* Standard-Rahmenvertrag: wird bei neuen Angeboten vorbelegt. */}
                            <label className="flex items-center gap-1.5 w-fit text-xs font-bold text-slate-500 cursor-pointer select-none" title="Bei neuen Angeboten für diesen Kunden automatisch vorbelegen">
                              <input type="radio" name="defaultFramework" checked={!!f.isDefault} onChange={() => setDefaultFrameworkContract(f.id)} className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-400/40" />
                              Als Standard-Rahmenvertrag
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <label className={labelClass}>Gültig von</label>
                                <input type="date" value={f.validFrom || ''} onChange={(e) => updateFrameworkContract(f.id, 'validFrom', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20" />
                              </div>
                              <div className="space-y-1">
                                <label className={labelClass}>Gültig bis</label>
                                <input type="date" value={f.validUntil || ''} onChange={(e) => updateFrameworkContract(f.id, 'validUntil', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20" />
                              </div>
                            </div>
                            <input value={f.note || ''} onChange={(e) => updateFrameworkContract(f.id, 'note', e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Notiz" />
                          </fieldset>
                          {itemCount > 0 && <FrameworkItemsTable contract={f} />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <fieldset disabled={readOnly} className="m-0 border-0 p-0 space-y-1.5 max-w-xl min-w-0">
                <label className={labelClass}>Interne Notizen</label>
                <textarea value={draft.notes || ''} onChange={(e) => set('notes', e.target.value)} rows={5} className={`${inputClass} resize-none`} placeholder="Interne Vermerke zum Kunden…" />
              </fieldset>
            </div>
          )}

          {/* BEWERTUNG */}
          {activeTab === 'bewertung' && (
            <fieldset disabled={readOnly} className="m-0 border-0 p-0 space-y-6 max-w-xl min-w-0">
              <div className="space-y-4">
                {([
                  ['ratingZahlungsmoral', 'Zahlungsmoral'],
                  ['ratingZusammenarbeit', 'Zusammenarbeit'],
                  ['ratingWirtschaftlichkeit', 'Wirtschaftlichkeit'],
                ] as const).map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium text-slate-600">{label}</span>
                    <CustomerRatingStars
                      value={draft[key] as number | undefined}
                      onChange={(v) => setRating(key, v)}
                      readOnly={readOnly}
                    />
                  </div>
                ))}
                {/* Gesamtbewertung: automatischer Durchschnitt, nicht klickbar. */}
                <div className="flex items-center justify-between py-2 mt-2 pt-4 border-t border-slate-100">
                  <span className="text-sm font-bold text-slate-800">Gesamtbewertung <span className="font-normal text-slate-400">(Ø automatisch)</span></span>
                  <div className="flex items-center gap-2">
                    <CustomerRatingStars value={draft.ratingOverall != null ? Math.round(draft.ratingOverall) : undefined} readOnly />
                    <span className="text-sm font-bold text-slate-700 tabular-nums w-9 text-right">
                      {draft.ratingOverall != null ? draft.ratingOverall.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className={labelClass}>Bewertungsnotizen</label>
                <textarea value={draft.ratingNotes || ''} onChange={(e) => set('ratingNotes', e.target.value)} rows={4} className={`${inputClass} resize-none`} placeholder="Begründung / Erfahrungen…" />
              </div>
            </fieldset>
          )}

          {/* PREISE */}
          {activeTab === 'preise' && (
            <div className="space-y-4">
              <p className="text-xs text-slate-500">
                Sonderpreise und Rabatte je Katalogposition. Leeres Feld = kein Sonderwert; der effektive Preis
                berücksichtigt sonst den Standardrabatt ({Number.isFinite(draft.defaultDiscountPercent) ? draft.defaultDiscountPercent : 0} %).
              </p>
              {pricesLoading ? (
                <div className="py-16 text-center text-slate-400">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-500" />
                  <p className="text-sm font-medium">Preise werden geladen…</p>
                </div>
              ) : catalog.length === 0 ? (
                <div className="py-16 text-center text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-100">
                  <Tag className="w-8 h-8 opacity-20 mx-auto mb-2" />
                  <p className="text-xs font-medium">Kein Leistungskatalog vorhanden</p>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-2">
                  <table className="w-full text-left border-collapse min-w-[720px]">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                        <th className="px-3 py-3 w-[70px]">LV-Pos</th>
                        <th className="px-3 py-3">Leistung</th>
                        <th className="px-3 py-3 w-[70px]">Einheit</th>
                        <th className="px-3 py-3 text-right w-[100px]">Katalogpreis</th>
                        <th className="px-3 py-3 w-[110px]">Sonderpreis</th>
                        <th className="px-3 py-3 w-[90px]">Rabatt %</th>
                        <th className="px-3 py-3 text-right w-[100px]">Effektiv</th>
                        <th className="px-2 py-3 w-[44px]"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {catalog.map((it) => {
                        const e = edits[it.id] || { unitPrice: '', discountPercent: '' };
                        const override: CustomerPrice | undefined =
                          e.unitPrice.trim() === '' && e.discountPercent.trim() === ''
                            ? undefined
                            : {
                                id: priceMap[it.id]?.id || '',
                                customerId: customer.id,
                                catalogItemId: it.id,
                                unitPrice: e.unitPrice.trim() === '' ? undefined : Number(e.unitPrice),
                                discountPercent: e.discountPercent.trim() === '' ? undefined : Number(e.discountPercent),
                              };
                        const effective = resolveUnitPrice(
                          it.unitPrice,
                          override,
                          Number.isFinite(draft.defaultDiscountPercent) ? draft.defaultDiscountPercent : 0
                        );
                        const hasSpecial = !!priceMap[it.id];
                        return (
                          <tr key={it.id} className="hover:bg-slate-50/60">
                            <td className="px-3 py-2.5 text-xs font-mono text-slate-400 align-middle">{it.lvPosition || '—'}</td>
                            <td className="px-3 py-2.5 text-sm text-slate-700 align-middle">{it.title}</td>
                            <td className="px-3 py-2.5 text-xs text-slate-400 align-middle">{it.unit}</td>
                            <td className="px-3 py-2.5 text-sm text-slate-500 text-right align-middle whitespace-nowrap">{fmtEur(it.unitPrice)}</td>
                            <td className="px-3 py-2.5 align-middle">
                              <input
                                type="number" min={0} step={0.01}
                                value={e.unitPrice}
                                onChange={(ev) => setEdit(it.id, 'unitPrice', ev.target.value)}
                                onBlur={() => persistRow(it.id)}
                                disabled={readOnly}
                                placeholder="—"
                                className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                              />
                            </td>
                            <td className="px-3 py-2.5 align-middle">
                              <input
                                type="number" min={0} max={100} step={0.5}
                                value={e.discountPercent}
                                onChange={(ev) => setEdit(it.id, 'discountPercent', ev.target.value)}
                                onBlur={() => persistRow(it.id)}
                                disabled={readOnly}
                                placeholder="—"
                                className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                              />
                            </td>
                            <td className="px-3 py-2.5 text-right align-middle whitespace-nowrap">
                              <span className={`text-sm font-bold ${hasSpecial ? 'text-blue-600' : 'text-slate-700'}`}>{fmtEur(effective)}</span>
                            </td>
                            <td className="px-2 py-2.5 text-right align-middle">
                              {savingRow === it.id ? (
                                <Loader2 className="w-4 h-4 animate-spin text-blue-500 ml-auto" />
                              ) : hasSpecial && !readOnly ? (
                                <button onClick={() => resetRow(it.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" title="Sonderpreis zurücksetzen">
                                  <RotateCcw className="w-4 h-4" />
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* HISTORIE – Angebote + Projekte/Auftraege (read-only) */}
          {activeTab === 'historie' && (
            <div className="space-y-8">
              {histLoading ? (
                <div className="py-16 text-center text-slate-400">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-500" />
                  <p className="text-sm font-medium">Historie wird geladen…</p>
                </div>
              ) : histError ? (
                <div className="py-12 text-center text-slate-400 bg-red-50/50 rounded-2xl border border-red-100">
                  <p className="font-bold text-slate-600">Fehler beim Laden</p>
                  <p className="text-xs mt-1 mb-4 text-slate-500">{histError}</p>
                  <Button variant="secondary" onClick={loadHistory} className="h-9 text-xs">Erneut versuchen</Button>
                </div>
              ) : (
                <>
                  {/* Stammdaten-Aenderungen */}
                  {(draft.changeLog?.length ?? 0) > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <History className="w-4 h-4 text-slate-400" />
                        <span className="text-sm font-bold text-slate-700">Stammdaten-Änderungen</span>
                        <span className="text-xs text-slate-400">({draft.changeLog!.length})</span>
                      </div>
                      <div className="space-y-2">
                        {[...draft.changeLog!].reverse().map((entry, i) => (
                          <div key={`${entry.at}-${i}`} className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-sm">
                            <div className="text-[11px] text-slate-400 mb-1">
                              {new Date(entry.at).toLocaleString('de-DE')}{entry.by ? ` · ${entry.by}` : ''}
                            </div>
                            <ul className="space-y-0.5">
                              {entry.changes.map((ch, j) => (
                                <li key={j} className="text-slate-600">
                                  <span className="font-bold text-slate-700">{ch.field}:</span>{' '}
                                  <span className="line-through text-slate-400">{ch.from || '—'}</span>
                                  {' → '}
                                  <span className="text-slate-800">{ch.to || '—'}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Angebote */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="text-sm font-bold text-slate-700">Angebote</span>
                      <span className="text-xs text-slate-400">({histQuotes.length})</span>
                    </div>
                    {histQuotes.length === 0 ? (
                      <div className="py-10 text-center text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-100">
                        <p className="text-xs font-medium">Noch keine Angebote für diesen Kunden</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto -mx-2">
                        <table className="w-full text-left border-collapse min-w-[640px]">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                              <th className="px-3 py-3">Nummer</th>
                              <th className="px-3 py-3">Titel</th>
                              <th className="px-3 py-3 text-right w-[110px]">Netto</th>
                              <th className="px-3 py-3 w-[120px]">Status</th>
                              <th className="px-3 py-3 w-[110px]">Datum</th>
                              <th className="px-3 py-3 w-[56px] text-center">PDF</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {histQuotes.map((q) => {
                              const eff: QuoteStatus =
                                q.status === 'sent' && isQuoteExpired(q, new Date().toISOString().slice(0, 10))
                                  ? 'expired'
                                  : q.status;
                              return (
                                <tr
                                  key={q.id}
                                  onClick={onOpenQuote ? () => onOpenQuote(q.id) : undefined}
                                  className={historyRowClass(Boolean(onOpenQuote))}
                                >
                                  <td className="px-3 py-2.5 text-sm font-bold text-slate-800 align-middle whitespace-nowrap">
                                    {/* Knopf ohne eigenen Handler: der Klick steigt zur Zeile
                                        auf. Er ist nur da, damit die Tastatur hinkommt. */}
                                    {onOpenQuote ? (
                                      <button type="button" className={historyLinkClass} title="Angebot öffnen">{q.quoteNumber}</button>
                                    ) : q.quoteNumber}
                                  </td>
                                  <td className="px-3 py-2.5 text-sm text-slate-600 align-middle truncate max-w-[220px]">{q.title || <span className="text-slate-300">—</span>}</td>
                                  <td className="px-3 py-2.5 text-sm font-bold text-slate-700 text-right align-middle whitespace-nowrap">
                                    {fmtEur(q.netTotal)}
                                    {quoteAmountSuffix(q) && <span className="ml-1 text-[11px] font-medium text-slate-400">{quoteAmountSuffix(q)}</span>}
                                  </td>
                                  <td className="px-3 py-2.5 align-middle">
                                    <div className="flex flex-wrap items-center gap-1">
                                      <QuoteStatusBadge status={eff} />
                                      {/* Angenommen ohne Projekt: die Preise liegen als
                                          Rahmenvertrag unter „Konditionen". */}
                                      {isFrameworkAcceptedQuote(q) && (
                                        <span
                                          className="inline-block px-2 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-[10px] font-bold text-indigo-700"
                                          title="Als Rahmenvertrag beim Kunden hinterlegt (Konditionen)"
                                        >
                                          Rahmenvertrag
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2.5 text-sm text-slate-500 align-middle whitespace-nowrap">{fmtDate(q.quoteDate)}</td>
                                  <td className="px-3 py-2.5 align-middle text-center">
                                    {/* Archivkopie entsteht erst beim Versand — Entwuerfe
                                        und Altbestand ohne Archiv haben kein PDF. */}
                                    {q.pdfUrl ? (
                                      <a
                                        href={q.pdfUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className={historyPdfClass}
                                        title="Versendetes Angebot als PDF öffnen"
                                        aria-label={`PDF zu ${q.quoteNumber} öffnen`}
                                      >
                                        <FileText className="w-4 h-4" />
                                      </a>
                                    ) : <span className="text-slate-300">—</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Rechnungen — inkl. Belege gemergter Quellkunden */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="text-sm font-bold text-slate-700">Rechnungen</span>
                      <span className="text-xs text-slate-400">({histInvoices.length})</span>
                    </div>
                    {invoicePdfError && (
                      <div className="bg-red-50 border border-red-100 text-red-700 rounded-xl p-3 text-xs flex items-start justify-between gap-3">
                        <span className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 shrink-0" />{invoicePdfError}</span>
                        <button type="button" onClick={() => setInvoicePdfError(null)} className="shrink-0 hover:bg-red-100 rounded p-0.5"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                    {histInvoices.length === 0 ? (
                      <div className="py-10 text-center text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-100">
                        <p className="text-xs font-medium">Noch keine Rechnungen für diesen Kunden</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto -mx-2">
                        <table className="w-full text-left border-collapse min-w-[640px]">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                              <th className="px-3 py-3">Nummer</th>
                              <th className="px-3 py-3 text-right w-[120px]">Brutto</th>
                              <th className="px-3 py-3 w-[150px]">Status</th>
                              <th className="px-3 py-3 w-[110px]">Datum</th>
                              <th className="px-3 py-3 w-[56px] text-center">PDF</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {histInvoices.map((inv) => (
                              <tr
                                key={inv.id}
                                onClick={onOpenInvoice ? () => onOpenInvoice(inv.id) : undefined}
                                className={historyRowClass(Boolean(onOpenInvoice))}
                              >
                                <td className="px-3 py-2.5 text-sm font-bold text-slate-800 align-middle whitespace-nowrap">
                                  {onOpenInvoice ? (
                                    <button type="button" className={historyLinkClass} title="Rechnung öffnen">
                                      {inv.invoiceNumber || 'Entwurf'}
                                    </button>
                                  ) : (
                                    inv.invoiceNumber || <span className="text-slate-400 font-normal">Entwurf</span>
                                  )}
                                  {mergedSourceNames.has(inv.customerId) && (
                                    <span className="block text-[11px] font-normal text-slate-400">
                                      vom zusammengeführten Kunden {mergedSourceNames.get(inv.customerId)}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 text-sm font-bold text-slate-700 text-right align-middle whitespace-nowrap">{formatCents(inv.totals.grossTotalCents)}</td>
                                {/* Der Ausweis will den BELEG und ein „heute" (Faelligkeit →
                                    ueberfaellig), nicht nur den gespeicherten Status. Mit
                                    `status=` kam gar kein Beleg an und die Zelle brach ab. */}
                                <td className="px-3 py-2.5 align-middle"><InvoiceStatusBadge invoice={inv} today={today} /></td>
                                <td className="px-3 py-2.5 text-sm text-slate-500 align-middle whitespace-nowrap">{fmtDate(inv.invoiceDate)}</td>
                                <td className="px-3 py-2.5 align-middle text-center">
                                  {/* PDF gibt es erst ab der Ausstellung (Archiv). */}
                                  {inv.pdfUrl ? (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); void openInvoicePdf(inv); }}
                                      disabled={openingInvoiceId === inv.id}
                                      className={historyPdfClass}
                                      title="Archiviertes PDF öffnen"
                                      aria-label={`PDF zu ${inv.invoiceNumber || 'diesem Beleg'} öffnen`}
                                    >
                                      {openingInvoiceId === inv.id
                                        ? <Loader2 className="w-4 h-4 animate-spin" />
                                        : <FileText className="w-4 h-4" />}
                                    </button>
                                  ) : <span className="text-slate-300">—</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Projekte / Auftraege */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-slate-400" />
                      <span className="text-sm font-bold text-slate-700">Projekte / Aufträge</span>
                      <span className="text-xs text-slate-400">({histProjects.length})</span>
                    </div>
                    {histProjects.length === 0 ? (
                      <div className="py-10 text-center text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-100">
                        <p className="text-xs font-medium">Noch keine Projekte für diesen Kunden</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto -mx-2">
                        <table className="w-full text-left border-collapse min-w-[560px]">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                              <th className="px-3 py-3 w-[130px]">Projektnummer</th>
                              <th className="px-3 py-3">Name</th>
                              <th className="px-3 py-3 w-[120px]">Status</th>
                              <th className="px-3 py-3 w-[120px]">Angelegt am</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {histProjects.map((p) => (
                              <tr
                                key={p.id}
                                onClick={onOpenProject ? () => onOpenProject({ id: p.id, name: p.name, status: p.status }) : undefined}
                                className={historyRowClass(Boolean(onOpenProject))}
                              >
                                <td className="px-3 py-2.5 text-sm font-mono font-bold text-slate-800 align-middle whitespace-nowrap">
                                  {onOpenProject ? (
                                    <button type="button" className={historyLinkClass} title="Projekt öffnen">{p.projectNumber || '—'}</button>
                                  ) : (p.projectNumber || '—')}
                                </td>
                                <td className="px-3 py-2.5 text-sm text-slate-600 align-middle truncate max-w-[240px]">{p.name || <span className="text-slate-300">—</span>}</td>
                                <td className="px-3 py-2.5 text-sm text-slate-500 align-middle whitespace-nowrap">{p.status || '—'}</td>
                                <td className="px-3 py-2.5 text-sm text-slate-500 align-middle whitespace-nowrap">{fmtDate(p.createdAt)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    </div>
  );
};
